import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { hasOfferRunOut } from '../../services/stateTimeout';
import { TemplateProps } from '../types';
import { QuasiSessionExpiredPage, QuasiStatusPage, QuasiWaitingPage } from './pages/QuasiStatusPage';
import QuasiVariantPage from './pages/QuasiVariantPage';

/**
 * The page of a quasi deposit. The client pays on the partner's checkout, so a ticket of this method
 * never reaches a receipt or its review: the page waits, offers other amounts when the asked one
 * cannot be paid, and reports the end. The move to the partner belongs to `TransactionPage`.
 */
const QuasiTemplate = ({
  transaction,
  loading,
  error,
  onDeclineOffer,
  onRedirect,
  onSelectVariant,
  variantSubmitting,
  setRetry,
}: TemplateProps) => {
  const { t } = useTranslation();

  // The last deadline of the offer outlives the response: the backend stops sending it as soon as the
  // matching reaper has expired the offer, and that is exactly when the page has to judge it.
  const lastOfferExpireAt = useRef<string | undefined>();
  // Whether the ticket left the offer because its time ran out. A pick or a refusal happens before
  // that moment. Read during render so a closing ticket never shows two different outcomes in a row.
  const offerLapsed = useRef(false);
  const offerExpireAt: string | undefined = (transaction as any)?.variantsExpireAt ?? undefined;
  if (transaction?.status === 'WAITING_FOR_VARIANT_SELECTION') {
    lastOfferExpireAt.current = offerExpireAt ?? lastOfferExpireAt.current;
    offerLapsed.current = false;
  } else if (transaction && lastOfferExpireAt.current) {
    offerLapsed.current = transaction.status === 'DECLINED' && hasOfferRunOut(lastOfferExpireAt.current);
    lastOfferExpireAt.current = undefined;
  }

  if (loading) {
    return <QuasiWaitingPage />;
  }

  const retry = { label: t('waiting.tryAgain'), onClick: () => setRetry((prev) => prev + 1) };

  // A failed request says nothing about the payment, and the page keeps polling a live ticket: only
  // a page with nothing to go on, or a search round that could not start, stops at this screen.
  if (!transaction) {
    return (
      <QuasiStatusPage
        mark='error'
        title={t('quasi.unavailableTitle')}
        text={t('quasi.unavailableText')}
        primary={retry}
      />
    );
  }

  const back = { label: t('quasi.back'), onClick: () => onRedirect(transaction.redirectUrl) };

  // The ticket can carry the partner's link in any status while the page is leaving for it.
  if (transaction.requisite?.type === 'redirect_url' && transaction.requisite?.paymentUrl) {
    return <QuasiWaitingPage />;
  }

  if (transaction.status === 'WAITING_FOR_VARIANT_SELECTION') {
    return (
      <QuasiVariantPage
        transaction={transaction}
        onDeclineOffer={onDeclineOffer}
        onSelectVariant={onSelectVariant}
        variantSubmitting={variantSubmitting}
        onBack={back.onClick}
        offerExpireAt={lastOfferExpireAt.current}
      />
    );
  }

  // A round of the search ended with nothing. The page does not poll this status, so the next round
  // starts from the client's click; past the attempts limit the backend declines the ticket. With no
  // round behind it the page is starting the first one on its own.
  if (transaction.status === 'CLIENT_PENDING' && (error || (transaction as any).paymentAdapterSearchAttemptsCounter > 0)) {
    return (
      <QuasiStatusPage
        mark='error'
        title={t('quasi.unavailableTitle')}
        text={t('quasi.unavailableText')}
        primary={retry}
        secondary={{ ...back, element: 'onepage-retry-back' }}
      />
    );
  }

  if (transaction.status === 'COMPLETED') {
    return <QuasiStatusPage mark='success' title={t('quasi.successTitle')} primary={back} />;
  }
  if (transaction.status === 'DECLINED') {
    return offerLapsed.current ? (
      <QuasiSessionExpiredPage onBack={back.onClick} />
    ) : (
      <QuasiStatusPage mark='error' title={t('quasi.failedTitle')} primary={back} />
    );
  }
  if (transaction.status === 'EXPIRED') {
    return <QuasiStatusPage mark='error' title={t('quasi.expiredTitle')} primary={back} />;
  }

  return <QuasiWaitingPage />;
};

export default QuasiTemplate;
